/**
 * Composer for a ticket interaction — a note, optionally with a call
 * recording attached.
 *
 * Notable behaviour:
 *   - "Spoke with" defaults to the customer, who is the counterparty on
 *     most support calls (it used to default to Packer).
 *   - Internal notes hide the call fields entirely; they are meaningless
 *     for a note nobody phoned.
 *   - Duration is read off the audio file instead of being typed in
 *     seconds, and the clip can be played back before it is committed.
 *   - Uploads report real progress; audio type and the 10 MB cap are
 *     stated up front rather than only on failure.
 *   - On a closed ticket the form starts collapsed, so a resolved case
 *     does not invite fresh contact logging by accident.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {  Mic, } from 'lucide-react';
// import { Loader2, Mic, Paperclip, Phone, Upload, X } from 'lucide-react';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useCreateCrmInteraction,
  type CrmAudience,
  type CrmTicket,
} from '@/api/bi/crm';
import { isOpenStatus } from '@/lib/crmStatus';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';
import type { TicketCapabilities } from '@/features/crm/useTicketCapabilities';
import { Skeleton } from '../ui/Skeleton';

// const MAX_BYTES = 10 * 1024 * 1024;
// const MAX_LABEL = '10 MB';

const AUDIENCE_OPTIONS: Array<{ value: CrmAudience; label: string }> = [
  { value: 'customer', label: 'Customer' },
  { value: 'packer', label: 'Packer' },
  { value: 'biller', label: 'Biller' },
  { value: 'rider', label: 'Rider' },
  { value: 'store_manager', label: 'Store Manager' },
  { value: 'internal', label: 'Internal note (nobody contacted)' },
];

interface Props {
  ticket: CrmTicket;
  capabilities: TicketCapabilities;
  /** Render without the surrounding Card — used when docked in the timeline. */
  embedded?: boolean;
}

export function CallRecordingUpload({ ticket, capabilities, embedded = false }: Props) {
  const ticketIsOpen = isOpenStatus(ticket.status);
  const [expanded, setExpanded] = useState(ticketIsOpen);
  const [audience, setAudience] = useState<CrmAudience>('customer');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [contactedName, setContactedName] = useState('');
  const [contactedPhone, setContactedPhone] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const [isOutgoing, setIsOutgoing] = useState(true);
  const [progress, setProgress] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
    const [dataUrl,setDataUrl] = useState('')

  const create = useCreateCrmInteraction(ticket.id);
  const isInternal = audience === 'internal';
  const canInteract = capabilities.canInteract;
  const fieldId = (name: string) => `interaction-${name}-${ticket.id}`;

const qrPayload = useMemo(() => {
      const user = getUserDetail();
      const payload =JSON.stringify({
      order: ticket.order?.id,
      phone: user?.phone,
      name: user?.name,
      role: audience,
      created_at: new Date(),
      user: user?.id,
      outgoing: isOutgoing,
      currentUser : null

    });

    return btoa(String.fromCharCode(...new TextEncoder().encode(payload)));

    
  }, [audience,isOutgoing]);
   useEffect(() => {

    const pk = getUserDetail();
    setContactedName(pk?.name??"")
    // setContactedPhone(pk?.phone??"")

  }, [audience])

    useEffect(() => {
        let cancelled = false;
        QRCode.toDataURL(qrPayload, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 220,
          color: { dark: '#0f172a', light: '#ffffff' },
        })
          .then((url) => {
            if (!cancelled) setDataUrl(url);
          })
          .catch(() => {
            if (!cancelled) setDataUrl('');
          });
        return () => {
          cancelled = true;
        };
      }, [qrPayload]);
     function getUserDetail(){
let pk
    switch(audience){
      case "packer":
        pk = ticket?.order?.packer
        break;
      case "rider":
        pk = ticket?.order?.rider;
        break;
      case "biller":
        pk = ticket?.order?.biller;
        break;
      case "customer":
        pk = ticket?.order?.customer;
        break;
    }
    return pk;
 }
  useEffect(() => {
    if (!filePreviewUrl) return;
    return () => URL.revokeObjectURL(filePreviewUrl);
  }, [filePreviewUrl]);

  function clearFile() {
    setFile(null);
    setFilePreviewUrl(null);
    setDuration(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  // function handleFile(next: File | null) {
  //   if (!next) {
  //     clearFile();
  //     return;
  //   }
  //   if (next.type && !next.type.startsWith('audio/')) {
  //     notify.error('Pick an audio file (m4a, mp3, wav, …).');
  //     return;
  //   }
  //   if (next.size > MAX_BYTES) {
  //     notify.error(`That recording is ${formatMb(next.size)} — the limit is ${MAX_LABEL}.`);
  //     return;
  //   }
  //   const url = URL.createObjectURL(next);
  //   setFile(next);
  //   setFilePreviewUrl(url);

  //   // Read the real length off the file so nobody has to count seconds.
  //   const probe = new Audio();
  //   probe.preload = 'metadata';
  //   probe.src = url;
  //   probe.addEventListener('loadedmetadata', () => {
  //     if (Number.isFinite(probe.duration) && probe.duration > 0) {
  //       setDuration(Math.round(probe.duration));
  //     }
  //   });
  // }

  function resetForm() {
    setNote('');
    setContactedName('');
    setContactedPhone('');
    clearFile();
    setProgress(null);
  }

  async function handleSubmit() {
    if (!note.trim() && !file) {
      notify.error('Add a note or attach a recording.');
      return;
    }
    try {
      console.log(progress)
      await create.mutateAsync({
        audience,
        note: note.trim(),
        file,
        contacted_name: isInternal ? '' : contactedName.trim(),
        contacted_phone: isInternal ? '' : contactedPhone.trim(),
        duration_seconds: isInternal ? null : duration,
        is_outgoing: isInternal ? undefined : isOutgoing,
        onProgress: file ? setProgress : undefined,
      });
      notify.success('Interaction recorded.');
      resetForm();
      if (!ticketIsOpen) setExpanded(false);
    } catch (err) {
      setProgress(null);
      notify.error(apiErrorMessage(err, 'Could not record the interaction.'));
    }
  }

  const body = !canInteract ? (
    <p className="text-xs text-muted-foreground">{capabilities.reason.edit}</p>
  ) : !expanded ? (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs text-muted-foreground">
        This ticket is closed. Anything logged now is recorded as a late interaction.
      </p>
      <Button variant="outline" size="sm" onClick={() => setExpanded(true)} className="gap-1">
        <Mic className="h-3.5 w-3.5" /> Log a late interaction
      </Button>
    </div>
  ) : (
    <div
      className="space-y-3"
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !create.isPending) {
          e.preventDefault();
          void handleSubmit();
        }
      }}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={fieldId('audience')} className="text-[11px] uppercase tracking-wide">
            Spoke with
          </Label>
          <Select value={audience} onValueChange={(v) => setAudience(v as CrmAudience)}>
            <SelectTrigger id={fieldId('audience')} className="h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUDIENCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!isInternal ? (
          <div className="space-y-1">
            <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Direction
            </span>
            <div
              role="radiogroup"
              aria-label="Call direction"
              className="flex h-9 items-center gap-1 rounded-md border border-input bg-surface p-1 text-xs"
            >
              {[
                { value: true, label: 'Outgoing' },
                { value: false, label: 'Incoming' },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  role="radio"
                  aria-checked={isOutgoing === opt.value}
                  onClick={() => setIsOutgoing(opt.value)}
                  className={cn(
                    'press flex-1 rounded-sm px-2 py-1 focus-ring',
                    isOutgoing === opt.value
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

        <div className=" py-2 flex justify-center ">
                           <div className='flex py-2 flex-col items-center'>
                            {dataUrl? (<label className="font-medium uppercase tracking-wide text-muted-foreground">{contactedName} </label>):<></>}
                             {dataUrl ? (
                              <img src={dataUrl} alt={`QR code for ticket #${ticket.id}`} className="h-[180px] w-[180px] bg-white border border-border p-2 rounded-md mt-2" />
                            ) : (
                              <Skeleton className="h-[180px] w-[180px]" />
                            )}
                           </div>
                          </div>
      {/* {!isInternal ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={fieldId('name')} className="text-[11px] uppercase tracking-wide">
              Contact name
            </Label>
            <input
              id={fieldId('name')}
              value={contactedName}
              onChange={(e) => setContactedName(e.target.value)}
              placeholder="Who picked up"
              className="h-9 w-full rounded-md border border-input bg-surface px-3 text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={fieldId('phone')} className="text-[11px] uppercase tracking-wide">
              Phone
            </Label>
            <input
              id={fieldId('phone')}
              value={contactedPhone}
              onChange={(e) => setContactedPhone(e.target.value)}
              placeholder="98…"
              className="h-9 w-full rounded-md border border-input bg-surface px-3 text-xs"
              inputMode="tel"
            />
          </div>
        </div>
      ) : null}

      <div className="space-y-1">
        <Label htmlFor={fieldId('note')} className="text-[11px] uppercase tracking-wide">
          Note
        </Label>
        <textarea
          id={fieldId('note')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Summarise the conversation, the root cause, or the next step."
          className="w-full rounded-md border border-input bg-surface px-3 py-2 text-xs"
        />
        <p className="text-[11px] text-muted-foreground">
          Saves to this timeline
          {ticket.order_id != null ? (
            <> and to order #{ticket.order_id}&rsquo;s remarks panel</>
          ) : null}
          . ⌘/Ctrl + Enter submits.
        </p>
      </div>

      {!isInternal ? (
        <div className="space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              className="gap-1"
            >
              <Paperclip className="h-3.5 w-3.5" />
              Attach recording
            </Button>
            <span className="text-[11px] text-muted-foreground">
              Audio only, up to {MAX_LABEL}
              {duration != null ? ` · detected length ${formatDuration(duration)}` : ''}
            </span>
          </div>

          {file ? (
            <div className="space-y-2 rounded-md border border-border bg-surface p-2">
              <div className="flex items-center gap-2 text-[11px]">
                <Phone className="h-3 w-3 text-muted-foreground" />
                <span className="max-w-[220px] truncate font-medium">{file.name}</span>
                <span className="text-muted-foreground">{formatMb(file.size)}</span>
                <button
                  type="button"
                  onClick={clearFile}
                  aria-label="Remove attached recording"
                  className="press ml-auto rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-ring"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              {filePreviewUrl ? (
                <audio
                  controls
                  preload="metadata"
                  src={filePreviewUrl}
                  className="h-9 w-full"
                  aria-label="Preview of the attached recording"
                />
              ) : null}
              {progress != null ? (
                <div className="space-y-1">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground" role="status" aria-live="polite">
                    Uploading… {progress}%
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null} */}

      <div className="flex items-center justify-end gap-2">
        {!ticketIsOpen ? (
          <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>
            Cancel
          </Button>
        ) : null}
        {/* <Button onClick={handleSubmit} disabled={create.isPending} size="sm" className="gap-1">
          {create.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          {create.isPending ? 'Saving…' : 'Save interaction'}
        </Button> */}
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-3">
        <p className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Mic className="h-4 w-4" /> Add interaction
        </p>
        {body}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Mic className="h-4 w-4" /> Add interaction
        </CardTitle>
      </CardHeader>
      <CardBody>{body}</CardBody>
    </Card>
  );
}

// function formatMb(bytes: number): string {
//   if (bytes < 1024) return `${bytes} B`;
//   if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
//   return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
// }

// function formatDuration(seconds: number): string {
//   const mins = Math.floor(seconds / 60);
//   const secs = seconds % 60;
//   return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
// }
